"use client"

import { useState, useEffect } from "react"
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Box,
  Grid,
  Paper,
  Tabs,
  Tab,
  Divider,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  alpha,
  useTheme,
  Card,
  CardContent,
  Button,
} from "@mui/material"
import CloseIcon from "@mui/icons-material/Close"
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet"
import ReceiptIcon from "@mui/icons-material/Receipt"
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart"
import PersonIcon from "@mui/icons-material/Person"
import BusinessIcon from "@mui/icons-material/Business"
import TrendingUpIcon from "@mui/icons-material/TrendingUp"
import TrendingDownIcon from "@mui/icons-material/TrendingDown"
import BalanceIcon from "@mui/icons-material/Balance"
import DownloadIcon from "@mui/icons-material/Download"
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf"
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  LineChart,
  Line,
} from "recharts"
import jsPDF from "jspdf"
import "jspdf-autotable"

// Componente para el panel de pestañas
function TabPanel(props) {
  const { children, value, index, ...other } = props

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`detalle-tabpanel-${index}`}
      aria-labelledby={`detalle-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 2 }}>{children}</Box>}
    </div>
  )
}

function a11yProps(index) {
  return {
    id: `detalle-tab-${index}`,
    "aria-controls": `detalle-tabpanel-${index}`,
  }
}

const ModalDetalleCuentaCorriente = ({ open, handleClose, cuentaCorriente }) => {
  const theme = useTheme()
  const [tabValue, setTabValue] = useState(0)
  const [itemsData, setItemsData] = useState([])
  const [transaccionesData, setTransaccionesData] = useState([])
  const [transaccionesStats, setTransaccionesStats] = useState({
    totalCompras: 0,
    totalPagos: 0,
    ultimaTransaccion: null,
  })

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue)
  }

  // Procesar datos cuando cambia la cuenta corriente seleccionada
  useEffect(() => {
    if (cuentaCorriente) {
      // Procesar transacciones
      const transacciones = Array.isArray(cuentaCorriente.transacciones) ? cuentaCorriente.transacciones : []
      const transaccionesItems = Array.isArray(cuentaCorriente.transaccionesItems)
        ? cuentaCorriente.transaccionesItems
        : []

      // Extraer todos los items de todas las transacciones
      const allItems = []
      transaccionesItems.forEach((tx) => {
        if (Array.isArray(tx.TransaccionItems)) {
          tx.TransaccionItems.forEach((item) => {
            if (item.item) {
              allItems.push({
                id: item.idItem,
                descripcion: item.item.descripcion || "Sin descripción",
                cantidad: item.cantidad || 0,
                precio: item.precio || 0,
                montoTotal: item.montoTotal || 0,
                fecha: tx.fechaHoraCreacion,
                idTransaccion: tx.id,
              })
            }
          })
        }
      })

      // Calcular estadísticas basadas en operacionParaCuentaCorriente
      // Transacciones que GENERAN deuda (ventas/compras)
      const operacionesDeuda = transacciones.filter(
        (t) => t.operacionParaCuentaCorriente !== 'operacionCaja'
      )
      // Transacciones que SALDAN deuda (pagos desde TablaCuentaCorriente)
      const operacionesPago = transacciones.filter(
        (t) => t.operacionParaCuentaCorriente === 'operacionCaja'
      )
      
      const totalCompras = operacionesDeuda.reduce((sum, t) => sum + Math.abs(t.montoTotal || 0), 0)
      const totalPagos = operacionesPago.reduce((sum, t) => sum + Math.abs(t.montoTotal || 0), 0)

      // Ordenar transacciones por fecha (más reciente primero)
      const sortedTransacciones = [...transacciones].sort(
        (a, b) => new Date(b.fecha).getTime() - new Date(a.fecha).getTime(),
      )

      setItemsData(allItems)
      setTransaccionesData(sortedTransacciones)
      setTransaccionesStats({
        totalCompras,
        totalPagos,
        ultimaTransaccion: sortedTransacciones[0] || null,
      })
    }
  }, [cuentaCorriente])

  // Formatear moneda
  const formatCurrency = (value) => {
    return new Intl.NumberFormat("es-AR", {
      style: "currency",
      currency: "ARS",
      maximumFractionDigits: 0,
    }).format(value)
  }

  // Formatear fecha
  const formatDate = (dateString) => {
    if (!dateString) return "N/A"
    return new Intl.DateTimeFormat("es-AR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(new Date(dateString))
  }

 // Función para generar PDF de transacción individual con estado de cuenta (versión mejorada)
const handleDownloadTransaction = (transaction) => {
  const doc = new jsPDF({ unit: "pt", format: "a4" })

  // ======= Tokens de diseño (Material-like) =======
  const PAL = {
    primary: [25, 118, 210],
    secondary: [120, 144, 156],
    success: [76, 175, 80],
    warning: [255, 152, 0],
    error: [244, 67, 54],
    ink: [33, 33, 33],
    muted: [117, 117, 117],
    inkLight: [158, 158, 158],
    bgSoft: [245, 247, 250]
  }
  const M = { l: 48, r: 48, t: 56, b: 56, gridGap: 14 }

  // ======= Helpers UI básicos =======
  const text = (str, x, y, { size = 10, color = PAL.ink, align = "left", bold = false } = {}) => {
    doc.setFont("helvetica", bold ? "bold" : "normal")
    doc.setFontSize(size)
    doc.setTextColor(...color)
    doc.text(str, x, y, { align })
  }

  const badge = (label, x, y, color = PAL.primary) => {
    // “Píldora” tipo chip
    doc.setFont("helvetica", "bold")
    doc.setFontSize(9)
    const padX = 8, padY = 4
    const w = doc.getTextWidth(label) + padX * 2
    const h = 16
    doc.setFillColor(...color)
    doc.roundedRect(x, y - h + 2, w, h, 8, 8, "F")
    doc.setTextColor(255, 255, 255)
    doc.text(label, x + padX, y - 5)
    doc.setTextColor(...PAL.ink)
  }

  const sectionTitle = (label, y) => {
    doc.setDrawColor(...PAL.primary)
    doc.setLineWidth(1)
    doc.line(M.l, y + 6, doc.internal.pageSize.getWidth() - M.r, y + 6)
    text(label, M.l, y, { size: 12, color: PAL.primary, bold: true })
  }

  const kvRow = (label, value) => ([
    { content: label, styles: { halign: "left", fontStyle: "bold", textColor: PAL.muted } },
    { content: value ?? "N/A", styles: { halign: "left" } }
  ])

  // ======= Header =======
  // Barra superior suave
  doc.setFillColor(...PAL.bgSoft)
  doc.rect(0, 0, doc.internal.pageSize.getWidth(), 70, "F")

  // Título
  text("DETALLE DE TRANSACCIÓN", doc.internal.pageSize.getWidth() / 2, 38, {
    size: 16, color: PAL.primary, align: "center", bold: true
  })

  // Subtítulo / metadata
  text("LUTENTE ERP · Sistema de Gestión", M.l, 58, { size: 10, color: PAL.inkLight })
  text(`Fecha de emisión: ${new Date().toLocaleDateString("es-AR")}`, doc.internal.pageSize.getWidth() - M.r, 58, {
    size: 10, color: PAL.inkLight, align: "right"
  })

  // "Chip" de tipo de transacción (basado en operacionParaCuentaCorriente)
  const esPago = transaction?.operacionParaCuentaCorriente === 'operacionCaja'
  const esCliente = (cuentaCorriente?.tipoEntidad || "").toLowerCase().includes("cliente")
  const labelEntidad = esCliente ? "Cliente" : "Proveedor"
  
  let tipoTx, tipoColor
  if (esPago) {
    // Operación que SALDA deuda
    tipoTx = esCliente ? "Cobro/Pago" : "Pago a Proveedor"
    tipoColor = PAL.success
  } else {
    // Operación que GENERA deuda
    tipoTx = esCliente ? "Venta/Deuda" : "Compra/Deuda"
    tipoColor = PAL.warning
  }
  
  badge(tipoTx.toUpperCase(), M.l, 84, tipoColor)

  // ======= Info Cliente y Resumen Cuenta (2 columnas) =======
  let cursorY = 120
  sectionTitle(`INFORMACIÓN DEL ${labelEntidad.toUpperCase()}`, cursorY); cursorY += 24

  // Columna izquierda (Cliente)
  const colWidth = (doc.internal.pageSize.getWidth() - M.l - M.r - M.gridGap) / 2
  doc.autoTable({
    startY: cursorY,
    margin: { left: M.l, right: doc.internal.pageSize.getWidth() - (M.l + colWidth) },
    theme: "plain",
    styles: { font: "helvetica", fontSize: 10, cellPadding: 3, textColor: PAL.ink },
    columnStyles: { 0: { cellWidth: 110 } },
    body: [
      kvRow("Nombre", `${cuentaCorriente.entidadCompleta?.descripcion || "N/A"} ${cuentaCorriente.entidadCompleta?.apellido || ""}`.trim()),
      kvRow("DNI/CUIT/CUIL", cuentaCorriente.entidadCompleta?.dniCuitCuil),
      kvRow("Teléfono", cuentaCorriente.entidadCompleta?.telefono),
      kvRow("Email", cuentaCorriente.entidadCompleta?.email),
      kvRow("Tipo de Entidad", cuentaCorriente.tipoEntidad)
    ]
  })

  // Columna derecha (Resumen de cuenta)
  const rightStartX = M.l + colWidth + M.gridGap
  const saldo = cuentaCorriente?.saldo || 0
  const saldoColor = saldo === 0 ? PAL.success : (saldo > 0 ? PAL.warning : PAL.error)
  doc.autoTable({
    startY: cursorY,
    margin: { left: rightStartX, right: M.r },
    theme: "plain",
    styles: { font: "helvetica", fontSize: 10, cellPadding: 3, textColor: PAL.ink },
    columnStyles: { 0: { cellWidth: 130 } },
    body: [
      kvRow("Saldo Actual", formatCurrency(saldo)),
      kvRow("Total Transacciones", String(transaccionesData.length || 0)),
      kvRow("Total Deuda Generada", formatCurrency(transaccionesStats.totalCompras || 0)),
      kvRow("Total Pagos Realizados", formatCurrency(transaccionesStats.totalPagos || 0)),
      kvRow("Última Transacción", transaccionesStats.ultimaTransaccion ? formatDate(transaccionesStats.ultimaTransaccion.fecha) : "N/A")
    ],
    didDrawCell: (data) => {
      // Pintar el valor de Saldo Actual con color según signo
      if (data.section === "body" && data.row.index === 0 && data.column.index === 1) {
        doc.setTextColor(...saldoColor)
        doc.text(data.cell.text[0], data.cell.x + 2, data.cell.y + data.cell.height / 2 + 3)
        doc.setTextColor(...PAL.ink)
        // Evitar doble impresión del texto por AutoTable
        data.cell.text = [""]
      }
    }
  })

  cursorY = Math.max(doc.lastAutoTable.finalY, cursorY) + 24

  // ======= Detalle de la Transacción =======
  sectionTitle("DETALLE DE LA TRANSACCIÓN", cursorY); cursorY += 32

  // Bloque de datos clave (2 columnas compactas)
  const txDesc = (transaction?.descripcion && String(transaction.descripcion).trim().length > 0)
    ? transaction.descripcion
    : "Sin descripción"

  // Calcular ancho disponible y ajustar columnas para evitar que el monto quede pegado al borde
  const pageWidth = doc.internal.pageSize.getWidth()
  const availableWidth = pageWidth - M.l - M.r
  // Distribuir el ancho en 4 columnas de manera proporcional, dejando más espacio a la derecha
  const colWidth1 = 100  // ID
  const colWidth2 = 120  // Fecha
  const colWidth3 = 100  // Tipo
  const colWidth4 = Math.max(140, availableWidth - colWidth1 - colWidth2 - colWidth3 - 20)  // Monto (con margen extra)

  doc.autoTable({
    startY: cursorY,
    margin: { left: M.l, right: M.r },
    theme: "plain",
    styles: { font: "helvetica", fontSize: 10, cellPadding: 4 },
    columnStyles: {
      0: { cellWidth: colWidth1 },
      1: { cellWidth: colWidth2 },
      2: { cellWidth: colWidth3 },
      3: { cellWidth: colWidth4, halign: "right" }  // Alinear monto a la derecha
    },
    body: [
      [
        { content: `ID: ${transaction?.id ?? "N/A"}`, styles: { fontStyle: "bold", textColor: PAL.muted } },
        { content: `Fecha: ${formatDate(transaction?.fecha)}`, styles: { fontStyle: "bold", textColor: PAL.muted } },
        { content: `Tipo: ${tipoTx}`, styles: { fontStyle: "bold", textColor: tipoColor } },
        { content: `Monto: ${formatCurrency(transaction?.montoTotal || 0)}`, styles: { fontStyle: "bold", textColor: PAL.ink, halign: "right" } }
      ],
      [
        { colSpan: 4, content: `Descripción: ${txDesc}`, styles: { halign: "left" } }
      ]
    ]
  })

  cursorY = doc.lastAutoTable.finalY + 18

  // ======= Items de la transacción (si existen) =======
  // Admite `transaction.TransaccionItems` o `transaction.items`
  const txItems = Array.isArray(transaction?.TransaccionItems)
    ? transaction.TransaccionItems
    : (Array.isArray(transaction?.items) ? transaction.items : [])

  if (txItems.length > 0) {
    sectionTitle("ÍTEMS", cursorY); cursorY += 20

    const itemsRows = txItems.map((it, idx) => {
      const desc = it?.item?.descripcion || it?.descripcion || "Item"
      const cant = Number(it?.cantidad || 0)
      const precio = Number(it?.precio || 0)
      const monto = Number(it?.montoTotal || (cant * precio)) || 0
      return [
        String(idx + 1).padStart(2, "0"),
        desc,
        String(cant),
        formatCurrency(precio),
        formatCurrency(monto)
      ]
    })

    doc.autoTable({
      startY: cursorY,
      margin: { left: M.l, right: M.r },
      theme: "striped",
      head: [["#", "Descripción", "Cant.", "P.U. (ref.)", "Total"]],
      body: itemsRows,
      styles: { font: "helvetica", fontSize: 9, cellPadding: 4 },
      headStyles: { fillColor: PAL.primary, textColor: [255, 255, 255] },
      columnStyles: {
        0: { cellWidth: 28, halign: "center" },
        1: { cellWidth: 270 },
        2: { cellWidth: 60, halign: "right" },
        3: { cellWidth: 100, halign: "right" },
        4: { cellWidth: 110, halign: "right" }
      }
    })

    cursorY = doc.lastAutoTable.finalY + 18
  }

  // ======= Últimas transacciones (mini estado) =======
  // Toma últimas 5 del contexto (del mismo cliente) para dar perspectiva
  if ((transaccionesData || []).length > 0) {
    sectionTitle(`ÚLTIMAS TRANSACCIONES DEL ${labelEntidad.toUpperCase()}`, cursorY); cursorY += 20

    const recent = transaccionesData.slice(0, 5).map((tx) => {
      const esPago = tx.operacionParaCuentaCorriente === 'operacionCaja'
      const tipoLabel = esPago 
        ? (esCliente ? "Cobro/Pago" : "Pago Proveedor")
        : (esCliente ? "Venta/Deuda" : "Compra/Deuda")
      
      return [
        String(tx.id),
        formatDate(tx.fecha),
        tipoLabel,
        formatCurrency(tx.montoTotal || 0)
      ]
    })

    doc.autoTable({
      startY: cursorY,
      margin: { left: M.l, right: M.r },
      theme: "grid",
      head: [["ID", "Fecha", "Tipo", "Monto"]],
      body: recent,
      styles: { font: "helvetica", fontSize: 9, cellPadding: 4 },
      headStyles: { fillColor: PAL.primary, textColor: [255, 255, 255] },
      columnStyles: {
        0: { cellWidth: 60, halign: "left" },
        1: { cellWidth: 150, halign: "left" },
        2: { cellWidth: 120, halign: "left" },
        3: { cellWidth: 120, halign: "right" }
      }
    })

    cursorY = doc.lastAutoTable.finalY + 18
  }

  // ======= Footer =======
  const pageW = doc.internal.pageSize.getWidth()
  const pageH = doc.internal.pageSize.getHeight()
  doc.setDrawColor(...PAL.secondary)
  doc.setLineWidth(0.4)
  doc.line(M.l, pageH - M.b, pageW - M.r, pageH - M.b)
  text("Documento informativo · No válido como comprobante fiscal", pageW / 2, pageH - M.b + 18, {
    size: 9, color: PAL.inkLight, align: "center"
  })
  text("Consultas: contactar al administrador del sistema.", pageW / 2, pageH - M.b + 32, {
    size: 9, color: PAL.inkLight, align: "center"
  })

  // Metadata + nombre de archivo
  doc.setProperties({
    title: `Transaccion_${transaction?.id || "s/n"}`,
    subject: "Detalle de transacción y estado de cuenta",
    author: "LUTENTE ERP",
    creator: "LUTENTE ERP"
  })

  const fileName = `transaccion_${transaction?.id}_${cuentaCorriente?.entidadCompleta?.dniCuitCuil || "cliente"}_${new Date().toISOString().slice(0,10)}.pdf`
  doc.save(fileName)
}

  // Datos para gráficos
  const prepareChartData = () => {
    // Agrupar transacciones por mes
    const monthlyData = {}
    transaccionesData.forEach((tx) => {
      const date = new Date(tx.fecha)
      const monthYear = `${date.getMonth() + 1}/${date.getFullYear()}`

      if (!monthlyData[monthYear]) {
        monthlyData[monthYear] = { deuda: 0, pagos: 0 }
      }

      const esPago = tx.operacionParaCuentaCorriente === 'operacionCaja'
      
      if (esPago) {
        // Transacción que SALDA deuda
        monthlyData[monthYear].pagos += Math.abs(tx.montoTotal || 0)
      } else {
        // Transacción que GENERA deuda
        monthlyData[monthYear].deuda += Math.abs(tx.montoTotal || 0)
      }
    })

    // Convertir a array para el gráfico
    return Object.entries(monthlyData).map(([month, data]) => ({
      month,
      deuda: data.deuda,
      pagos: data.pagos,
      balance: data.pagos - data.deuda,
    }))
  }

  // Datos para el gráfico de productos más comprados
  const prepareItemsChartData = () => {
    // Agrupar por descripción de item
    const itemsCount = {}
    itemsData.forEach((item) => {
      const desc = item.descripcion
      if (!itemsCount[desc]) {
        itemsCount[desc] = { count: 0, total: 0 }
      }
      itemsCount[desc].count += item.cantidad || 0
      itemsCount[desc].total += item.montoTotal || 0
    })

    // Convertir a array y ordenar por cantidad
    return Object.entries(itemsCount)
      .map(([name, data]) => ({
        name,
        cantidad: data.count,
        total: data.total,
      }))
      .sort((a, b) => b.total - a.total)
      .slice(0, 5) // Top 5 productos
  }

  const chartData = prepareChartData()
  const itemsChartData = prepareItemsChartData()
  const esCliente = (cuentaCorriente?.tipoEntidad || "").toLowerCase().includes("cliente")
  const etiquetaEntidad = esCliente ? "Cliente" : "Proveedor"
  const EntidadIcon = esCliente ? PersonIcon : BusinessIcon

  // Colores para gráficos
  const COLORS = [
    theme.palette.primary.main,
    theme.palette.secondary.main,
    theme.palette.error.main,
    theme.palette.warning.main,
    theme.palette.info.main,
    theme.palette.success.main,
  ]

  if (!cuentaCorriente) return null

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ m: 0, p: 2, bgcolor: theme.palette.primary.main, color: "white" }}>
        Detalle de Cuenta Corriente
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: "white",
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ p: 3 }}>
        {/* Información del cliente */}
        <Box sx={{ mb: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ display: "flex", alignItems: "center" }}>
                    <EntidadIcon sx={{ mr: 1 }} /> Información del {etiquetaEntidad}
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        Nombre:
                      </Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {cuentaCorriente.entidadCompleta?.descripcion || "N/A"}{" "}
                        {cuentaCorriente.entidadCompleta?.apellido || ""}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        DNI/CUIT/CUIL:
                      </Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {cuentaCorriente.entidadCompleta?.dniCuitCuil || "N/A"}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        Teléfono:
                      </Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {cuentaCorriente.entidadCompleta?.telefono || "N/A"}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        Email:
                      </Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {cuentaCorriente.entidadCompleta?.email || "N/A"}
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="body2" color="text.secondary">
                        Tipo de Entidad:
                      </Typography>
                      <Chip
                        icon={
                          esCliente ? (
                            <PersonIcon fontSize="small" />
                          ) : (
                            <BusinessIcon fontSize="small" />
                          )
                        }
                        label={cuentaCorriente.tipoEntidad || "N/A"}
                        size="small"
                        sx={{
                          mt: 0.5,
                          bgcolor: alpha(theme.palette.info.main, 0.1),
                          color: theme.palette.info.main,
                        }}
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ display: "flex", alignItems: "center" }}>
                    <AccountBalanceWalletIcon sx={{ mr: 1 }} /> Resumen de Cuenta
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        Saldo Actual:
                      </Typography>
                      <Chip
                        icon={
                          cuentaCorriente.saldo === 0 ? (
                            <BalanceIcon fontSize="small" />
                          ) : cuentaCorriente.saldo > 0 ? (
                            <TrendingUpIcon fontSize="small" />
                          ) : (
                            <TrendingDownIcon fontSize="small" />
                          )
                        }
                        label={formatCurrency(cuentaCorriente.saldo || 0)}
                        size="small"
                        sx={{
                          mt: 0.5,
                          bgcolor:
                            cuentaCorriente.saldo === 0
                              ? alpha(theme.palette.success.main, 0.1)
                              : cuentaCorriente.saldo > 0
                                ? alpha(theme.palette.warning.main, 0.1)
                                : alpha(theme.palette.error.main, 0.1),
                          color:
                            cuentaCorriente.saldo === 0
                              ? theme.palette.success.main
                              : cuentaCorriente.saldo > 0
                                ? theme.palette.warning.main
                                : theme.palette.error.main,
                          fontWeight: "bold",
                        }}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        Total Transacciones:
                      </Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {transaccionesData.length}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        Total Deuda Generada:
                      </Typography>
                      <Typography variant="body1" fontWeight="medium" color={theme.palette.warning.main}>
                        {formatCurrency(transaccionesStats.totalCompras)}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        Total Pagos Realizados:
                      </Typography>
                      <Typography variant="body1" fontWeight="medium" color={theme.palette.success.main}>
                        {formatCurrency(transaccionesStats.totalPagos)}
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="body2" color="text.secondary">
                        Última Transacción:
                      </Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {transaccionesStats.ultimaTransaccion
                          ? formatDate(transaccionesStats.ultimaTransaccion.fecha)
                          : "N/A"}
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>

        {/* Pestañas */}
        <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
          <Tabs value={tabValue} onChange={handleTabChange} aria-label="detalle tabs" centered>
            <Tab label="Transacciones" icon={<ReceiptIcon />} iconPosition="start" {...a11yProps(0)} />
            <Tab label="Productos" icon={<ShoppingCartIcon />} iconPosition="start" {...a11yProps(1)} />
            <Tab label="Estadísticas" icon={<TrendingUpIcon />} iconPosition="start" {...a11yProps(2)} />
          </Tabs>
        </Box>

        {/* Panel de Transacciones */}
        <TabPanel value={tabValue} index={0}>
          <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Fecha</TableCell>
                  <TableCell>Tipo</TableCell>
                  <TableCell align="right">Monto</TableCell>
                  <TableCell>Descripción</TableCell>
                  <TableCell align="center">Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {transaccionesData.map((tx) => (
                  <TableRow
                    key={tx.id}
                    sx={{
                      backgroundColor:
                        tx.idTipoTransaccion === 1
                          ? alpha(theme.palette.warning.main, 0.05)
                          : alpha(theme.palette.success.main, 0.05),
                      "&:hover": {
                        backgroundColor:
                          tx.idTipoTransaccion === 1
                            ? alpha(theme.palette.warning.main, 0.1)
                            : alpha(theme.palette.success.main, 0.1),
                      },
                    }}
                  >
                    <TableCell>{tx.id}</TableCell>
                    <TableCell>{formatDate(tx.fecha)}</TableCell>
                    <TableCell>
                      {(() => {
                        const esPago = tx.operacionParaCuentaCorriente === 'operacionCaja';
                        const esCliente = cuentaCorriente.tipoEntidad?.includes('Cliente');
                        
                        let label, color;
                        if (esPago) {
                          // Operación que SALDA deuda
                          label = esCliente ? "Cobro/Pago" : "Pago Proveedor";
                          color = "success";
                        } else {
                          // Operación que GENERA deuda
                          label = esCliente ? "Venta/Deuda" : "Compra/Deuda";
                          color = "warning";
                        }
                        
                        return (
                          <Chip
                            label={label}
                            size="small"
                            color={color}
                            variant="outlined"
                          />
                        );
                      })()}
                    </TableCell>
                    <TableCell align="right">{formatCurrency(tx.montoTotal || 0)}</TableCell>
                    <TableCell>{tx.descripcion || "Sin descripción"}</TableCell>
                    <TableCell align="center">
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<PictureAsPdfIcon />}
                        onClick={() => handleDownloadTransaction(tx)}
                        sx={{
                          minWidth: 'auto',
                          px: 1,
                          py: 0.5,
                          fontSize: '0.75rem',
                          color: theme.palette.error.main,
                          borderColor: theme.palette.error.main,
                          '&:hover': {
                            borderColor: theme.palette.error.dark,
                            backgroundColor: alpha(theme.palette.error.main, 0.1)
                          }
                        }}
                      >
                        PDF
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
                {transaccionesData.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      No hay transacciones disponibles
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        {/* Panel de Productos */}
        <TabPanel value={tabValue} index={1}>
          <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Producto</TableCell>
                  <TableCell align="right">Cantidad</TableCell>
                  <TableCell align="right">Precio</TableCell>
                  <TableCell align="right">Total</TableCell>
                  <TableCell>Fecha</TableCell>
                  <TableCell>ID Transacción</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {itemsData.map((item, index) => (
                  <TableRow
                    key={index}
                    sx={{
                      "&:hover": {
                        backgroundColor: alpha(theme.palette.primary.main, 0.05),
                      },
                    }}
                  >
                    <TableCell>{item.descripcion}</TableCell>
                    <TableCell align="right">{item.cantidad}</TableCell>
                    <TableCell align="right">{formatCurrency(item.precio || 0)}</TableCell>
                    <TableCell align="right">{formatCurrency(item.montoTotal || 0)}</TableCell>
                    <TableCell>{formatDate(item.fecha)}</TableCell>
                    <TableCell>{item.idTransaccion}</TableCell>
                  </TableRow>
                ))}
                {itemsData.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      No hay productos disponibles
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        {/* Panel de Estadísticas */}
        <TabPanel value={tabValue} index={2}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom align="center">
                  Evolución de Cuenta
                </Typography>
                <Box sx={{ height: 300 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" />
                      <YAxis tickFormatter={(value) => formatCurrency(value)} />
                      <RechartsTooltip formatter={(value) => [formatCurrency(value), ""]} />
                      <Legend />
                      <Line
                        type="monotone"
                        dataKey="deuda"
                        stroke={theme.palette.warning.main}
                        name="Deuda Generada"
                        strokeWidth={2}
                      />
                      <Line
                        type="monotone"
                        dataKey="pagos"
                        stroke={theme.palette.success.main}
                        name="Pagos Realizados"
                        strokeWidth={2}
                      />
                      <Line
                        type="monotone"
                        dataKey="balance"
                        stroke={theme.palette.info.main}
                        name="Balance"
                        strokeWidth={2}
                        strokeDasharray="5 5"
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </Box>
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom align="center">
                  Productos Más Comprados
                </Typography>
                <Box sx={{ height: 300 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={itemsChartData}
                      layout="vertical"
                      margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" tickFormatter={(value) => formatCurrency(value)} />
                      <YAxis type="category" dataKey="name" width={150} tick={{ fontSize: 12 }} />
                      <RechartsTooltip
                        formatter={(value, name, props) => {
                          if (name === "total") return [formatCurrency(value), "Total"]
                          return [value, "Cantidad"]
                        }}
                      />
                      <Legend />
                      <Bar dataKey="total" name="Monto Total" fill={theme.palette.primary.main} />
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </TabPanel>
      </DialogContent>
    </Dialog>
  )
}

export default ModalDetalleCuentaCorriente
